import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Products.css';
import Footer from './Footer';
import ContactGroceryApp from './ContactGroceryApp';
import SEO from './SEO';

const GroceryApp = () => {
  const [isSectionVisible, setIsSectionVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const imageWrapperRef = useRef(null);
  const sliderRef = useRef(null);
  const showcaseSectionRef = useRef(null);

  // Mobile app screenshots
  const mobileAppImages = [
    { id: 1, src: '/images/mobileapp/1.png', alt: 'App Screen 1' },
    { id: 2, src: '/images/mobileapp/2.png', alt: 'App Screen 2' },
    { id: 3, src: '/images/mobileapp/3.png', alt: 'App Screen 3' },
    { id: 4, src: '/images/mobileapp/4.png', alt: 'App Screen 4' },
    { id: 5, src: '/images/mobileapp/5.png', alt: 'App Screen 5' },
    { id: 6, src: '/images/mobileapp/6.png', alt: 'App Screen 6' },
    { id: 7, src: '/images/mobileapp/7.png', alt: 'App Screen 7' },
    { id: 8, src: '/images/mobileapp/8.png', alt: 'App Screen 8' },
    { id: 9, src: '/images/mobileapp/9.png', alt: 'App Screen 9' },
    { id: 10, src: '/images/mobileapp/10.png', alt: 'App Screen 10' },
    { id: 11, src: '/images/mobileapp/11.png', alt: 'App Screen 11' },
  ];

  const scrollSlider = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 280;
      sliderRef.current.scrollBy({
        left: direction === 'next' ? scrollAmount : -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Scroll-hijacking slider effect with smooth animation
  useEffect(() => {
    let rafId = null;
    let currentPos = 0;
    let targetPos = 0;

    const animate = () => {
      if (!sliderRef.current) return;
      
      // Smooth easing
      currentPos += (targetPos - currentPos) * 0.12;
      sliderRef.current.scrollLeft = currentPos;
      
      rafId = requestAnimationFrame(animate);
    };

    const handleScroll = () => {
      if (!showcaseSectionRef.current || !sliderRef.current) return;

      const section = showcaseSectionRef.current;
      const slider = sliderRef.current;
      
      // Get section bounds
      const rect = section.getBoundingClientRect();
      const sectionHeight = section.offsetHeight;
      const viewportHeight = window.innerHeight;
      
      // Calculate how far we've scrolled through the section
      // When section top hits viewport top, progress starts
      // When section bottom hits viewport bottom, progress ends
      const scrollableDistance = sectionHeight - viewportHeight;
      const scrolled = -rect.top;
      
      // Calculate progress (0 to 1)
      const progress = Math.max(0, Math.min(1, scrolled / scrollableDistance));
      
      // Calculate target scroll position
      const maxScroll = slider.scrollWidth - slider.clientWidth;
      targetPos = progress * maxScroll;
    };

    // Start animation loop
    rafId = requestAnimationFrame(animate);
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsSectionVisible(true);
            sectionObserver.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (sectionRef.current) {
      sectionObserver.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        sectionObserver.unobserve(sectionRef.current);
      }
    };
  }, []);

  const features = [
    {
      id: 1,
      title: '10-Minute Delivery',
      description: 'Lightning-fast delivery system that gets groceries to customers in minutes, not hours. Optimized routing and real-time tracking for maximum efficiency.',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      color: '#11998e'
    },
    {
      id: 2,
      title: 'Smart Inventory Management',
      description: 'AI-powered inventory tracking with automated restocking, expiry alerts, and real-time stock updates to ensure fresh products are always available.',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      color: '#f5576c'
    },
    {
      id: 3,
      title: 'Seamless Order Experience',
      description: 'Intuitive mobile app with quick reorder, smart recommendations, and multiple payment options for a frictionless shopping experience.',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      color: '#4facfe'
    }
  ];

  const products = [
    {
      id: 1,
      name: 'Mobile App for Customers',
      description: 'Beautiful, fast mobile application for iOS and Android that makes grocery shopping effortless and enjoyable.',
      image: '/images/resource/offer-2.jpg',
      category: 'Mobile App'
    },
    {
      id: 2,
      name: 'Delivery Partner App',
      description: 'Dedicated app for delivery executives with optimized routes, real-time navigation, and earnings tracking.',
      image: '/images/resource/offer-3.jpg',
      category: 'Delivery Management'
    },
    {
      id: 3,
      name: 'Vendor Dashboard',
      description: 'Comprehensive dashboard for store managers to track orders, manage inventory, and monitor performance metrics.',
      image: '/images/resource/offer-4.jpg',
      category: 'Business Intelligence'
    },
    {
      id: 4,
      name: 'Admin Panel',
      description: 'Powerful admin interface to manage multiple stores, delivery zones, pricing, and business operations.',
      image: '/images/resource/offer-1.jpg',
      category: 'Admin Tools'
    },
    {
      id: 5,
      name: 'Real-Time Analytics',
      description: 'Track sales, customer behavior, popular products, and delivery performance with comprehensive analytics.',
      image: '/images/main-slider/iot.jpg',
      category: 'Analytics'
    },
    {
      id: 6,
      name: 'Payment Gateway Integration',
      description: 'Secure payment processing with support for UPI, cards, wallets, and cash on delivery options.',
      image: '/images/resource/about-1.jpg',
      category: 'Payments'
    }
  ];

  const capabilities = [
    {
      title: 'Instant Order Fulfillment',
      description: 'Process and dispatch orders within minutes with automated picking, packing, and dispatch workflows.',
      image: '/images/resource/about-2.jpg'
    },
    {
      title: 'Multi-Store Management',
      description: 'Manage multiple dark stores or warehouses from a single platform with centralized inventory and order management.',
      image: '/images/resource/about-3.png'
    },
    {
      title: 'Customer Loyalty Programs',
      description: 'Build customer retention with rewards, discounts, and personalized offers based on shopping patterns.',
      image: '/images/resource/about-6.jpg'
    }
  ];

  return (
    <>
      <SEO 
        title="Quick Commerce Grocery App - 10-Minute Delivery Platform"
        description="Launch your quick commerce grocery business with our complete platform. 10-minute delivery, customer app, delivery partner app, vendor dashboard, real-time tracking, and smart inventory management."
        keywords="grocery app, quick commerce, 10-minute delivery, grocery delivery app, dark store, instant delivery, food delivery platform, grocery ordering system"
        url="/grocery-app"
        product={{
          name: "Quick Commerce Grocery App",
          description: "Complete quick commerce platform enabling 10-minute grocery deliveries with customer app, delivery partner app, and vendor dashboard.",
          operatingSystem: "Web, iOS, Android",
          features: "10-minute delivery, Real-time tracking, Smart inventory, Multi-store management, Payment gateway integration"
        }}
      />
      <section 
        className="products-hero products-hero--grocery"
        style={{ backgroundImage: `linear-gradient(90deg, rgba(0, 0, 0, 0.15) 0%, transparent 50%), url(${process.env.PUBLIC_URL || ''}/images/app1.png)` }}
      >
        <div className="container">
          <div className="products-hero-content">
            <h4>Quick Commerce Platform</h4>
            <h1>Groceries Delivered in Minutes, Not Hours.</h1>
            <p className="products-hero-subtitle">
            Transform grocery shopping with our quick commerce solution.
            Fast delivery, fresh products, and seamless experience—all powered by cutting-edge technology.
            </p>
            <div className="products-hero-cta">
              <button 
                className="cta-button primary" 
                onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
              >
                CONTACT SALES
              </button>
              <button 
                className="cta-button secondary" 
                onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
              >
                Get a Demo
              </button>
            </div>
          </div>
        </div>
      </section>
      <section className="products-intro">
        <div className="container">
          <div className="products-intro-content">
            <h2>Everything you need to launch and scale your quick commerce business.</h2>
            <div className="products-features-grid">
              {features.map((feature) => {
                // Convert hex color to RGB for glow effect
                const hex = feature.color.replace('#', '');
                const r = parseInt(hex.substr(0, 2), 16);
                const g = parseInt(hex.substr(2, 2), 16);
                const b = parseInt(hex.substr(4, 2), 16);
                
                return (
                  <div 
                    key={feature.id} 
                    className="products-feature-card"
                    style={{
                      '--glow-color': feature.color,
                      '--glow-rgb': `${r}, ${g}, ${b}`
                    }}
                  >
                    <h3 style={{ color: feature.color }}>{feature.title}</h3>
                    <p>{feature.description}</p>
                    <div className="feature-card-accent" style={{ background: feature.gradient }}></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <section className="pos-showcase pos-showcase-scroll-hijack" ref={showcaseSectionRef}>
        <div className="pos-showcase-sticky">
          <div className="pos-showcase-header">
            <div className="showcase-tag">GROCERY APP DEMO</div>
            <h1>Experience Lightning-Fast Grocery Delivery.</h1>
            <p className="showcase-description">
              See how our quick commerce platform enables 10-minute deliveries with intelligent routing, real-time inventory, and seamless customer experience.
            </p>
          </div>
          <div className="pos-showcase-container">
            <div className="mobile-app-slider-wrapper">
              <button 
                className="slider-nav-btn slider-prev" 
                onClick={() => scrollSlider('prev')}
                aria-label="Previous"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <div className="mobile-app-slider" ref={sliderRef}>
                {mobileAppImages.map((image) => (
                  <div key={image.id} className="mobile-app-slide">
                    <div className="mobile-app-phone-frame">
                      <img 
                        src={image.src} 
                        alt={image.alt}
                        loading="lazy"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <button 
                className="slider-nav-btn slider-next" 
                onClick={() => scrollSlider('next')}
                aria-label="Next"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section ref={sectionRef} className="vendor-ledger-section" style={{marginTop: '100px'}}>
        <div className="container">
          <div 
            ref={headerRef}
            className={`vendor-ledger-header ${isSectionVisible ? 'header-visible' : ''}`}
          >
            <h2 className="vendor-ledger-title">Order Management System</h2>
            <p className="vendor-ledger-description">
              Complete order lifecycle management from cart to delivery. Track orders, manage inventory, handle payments, and optimize delivery routes—all in one powerful system.
            </p>
          </div>
        </div>
        <div className={`vendor-ledger-showcase ${isSectionVisible ? 'animate-background' : ''}`}>
          {/* Background scrolling images */}
          <div className="vendor-ledger-background-images">
            <div className="bg-image bg-image-1">
              <div className="bg-card expense-report-card">
                <div className="bg-card-header">Active Orders</div>
                <div className="bg-card-content">
                  <div className="bg-card-amount">12 Orders</div>
                  <div className="bg-card-tag pending">In Progress</div>
                  <div className="bg-card-flow">
                    <div className="flow-avatar"></div>
                    <div className="flow-avatar approved"></div>
                    <div className="flow-avatar"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-image bg-image-2">
              <div className="bg-card payment-mode-card">
                <div className="bg-card-header">Payment Methods</div>
                <div className="bg-card-content">
                  <div className="bg-card-item">
                    <span className="bg-icon">💳</span>
                    <span>UPI</span>
                  </div>
                  <div className="bg-card-item">
                    <span className="bg-icon">🏦</span>
                    <span>Card Payment</span>
                  </div>
                  <div className="bg-card-item">
                    <span className="bg-icon">💰</span>
                    <span>Cash on Delivery</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-image bg-image-3">
              <div className="bg-card mobile-card">
                <div className="bg-card-header">DELIVERY</div>
                <div className="bg-card-content mobile-content">
                  <div className="mobile-merchant">FRESH GROCERY</div>
                  <div className="mobile-receipt"></div>
                </div>
              </div>
            </div>
            <div className="bg-image bg-image-4">
              <div className="bg-card payroll-card">
                <div className="bg-card-header">TRACKING</div>
                <div className="bg-card-content payroll-content">
                  <div className="payroll-avatar"></div>
                  <div className="payroll-text">Live Location</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Main centered image */}
          <div 
            ref={imageWrapperRef}
            className={`vendor-ledger-image-wrapper ${isSectionVisible ? 'image-scrolled' : ''}`}
          >
            <img 
              src="/images/vendor_ledger.jpg" 
              alt="Order Management System" 
              className="vendor-ledger-image"
            />
          </div>
        </div>
      </section>

      <ContactGroceryApp />
      <Footer />
    </>
  );
};

export default GroceryApp;
